import type { CatalogEntry } from '@sivan/shared';
import { useId, useRef, useState } from 'react';

export interface PickedValue {
  /** The chosen catalog entry, or null (nothing chosen, or a value recorded before catalogs). */
  readonly id: string | null;
  /** The text shown for it: the recorded text, or the entry's name. */
  readonly text: string;
}

const MAX_OPTIONS = 50;

/**
 * A searchable selector over a project catalog (ARIA combobox with a list box). Only active
 * entries can be chosen; a finding that already references an inactive entry keeps showing it.
 */
export function CatalogPicker({
  label,
  entries,
  value,
  onChange,
  clearable = false,
  error,
  hint,
}: {
  readonly label: string;
  readonly entries: readonly CatalogEntry[];
  readonly value: PickedValue;
  readonly onChange: (entry: CatalogEntry | null) => void;
  readonly clearable?: boolean;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const current = entries.find((e) => e.id === value.id);
  const shown = query ?? value.text;
  const needle = (query ?? '').trim().toLowerCase();
  const matches = entries
    .filter((e) => e.active && (needle === '' || e.name.toLowerCase().includes(needle)))
    .slice(0, MAX_OPTIONS);
  const options: (CatalogEntry | null)[] = clearable ? [null, ...matches] : matches;

  const close = () => {
    setOpen(false);
    setQuery(null);
  };
  const choose = (entry: CatalogEntry | null) => {
    onChange(entry);
    close();
  };
  const optionId = (index: number) => `${id}-option-${String(index)}`;
  const describedBy = [
    error === undefined ? null : `${id}-error`,
    hint === undefined ? null : `${id}-hint`,
  ]
    .filter((x) => x !== null)
    .join(' ');

  return (
    <div className="field picker">
      <label htmlFor={`${id}-input`}>{label}</label>
      <div className="picker__control">
        <input
          ref={input}
          id={`${id}-input`}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && options.length > 0 ? optionId(active) : undefined}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          placeholder="הקלידו לחיפוש או בחרו מהרשימה"
          value={shown}
          onFocus={() => {
            setOpen(true);
            setActive(0);
          }}
          onClick={() => {
            setOpen(true);
          }}
          onBlur={() => {
            // A click on an option fires before the blur completes (mousedown is prevented).
            close();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(a + 1, options.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && open) {
              e.preventDefault();
              const option = options[active];
              if (option !== undefined) choose(option);
            } else if (e.key === 'Escape' && open) {
              e.preventDefault();
              e.stopPropagation();
              close();
            }
          }}
        />
        <button
          type="button"
          className="picker__toggle"
          tabIndex={-1}
          aria-label={`פתיחת הרשימה: ${label}`}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            if (open) close();
            else {
              setOpen(true);
              input.current?.focus();
            }
          }}
        >
          ▾
        </button>
      </div>
      {open && (
        <ul className="picker__list" role="listbox" id={listId} aria-label={label}>
          {options.length === 0 ? (
            <li className="picker__empty" role="presentation">
              לא נמצאו התאמות. ניתן להוסיף ערכים ב"הגדרות מסירות" בנתוני האב.
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={option?.id ?? 'none'}
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                className={`picker__option${option === null ? ' picker__option--none' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onMouseEnter={() => {
                  setActive(index);
                }}
                onClick={() => {
                  choose(option);
                }}
              >
                {option === null ? 'ללא' : option.name}
              </li>
            ))
          )}
        </ul>
      )}
      {current !== undefined && !current.active && (
        <p className="hint">ערך זה הושבת בנתוני האב; הוא נשמר בליקוי כפי שנרשם.</p>
      )}
      {value.id === null && value.text !== '' && (
        <p className="hint">נרשם כטקסט חופשי לפני שהוגדרו רשימות. ניתן לבחור ערך מהרשימה.</p>
      )}
      {hint !== undefined && (
        <p className="hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p className="field__error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
